import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useLayoutEffect,
  useCallback,
  useEffect
} from "react";
import PropTypes from "prop-types";
import { Trans } from "@lingui/macro";
import dayjs from "dayjs";

import {
  FloatingMenu,
  FLOATING_MENU_POSITION
} from "../../inputs/dropdown/floatingmenu";
import { MenuItem } from "../../inputs/dropdown/menu";
import { Link } from "../../navigation/link";
import { Chip, CHIP_SIZES } from "../../datadisplay/chip";
import { NUMBER_FIELDS } from "../../common/libs/document.parser.service";

import styles from "./style.css";

export {
  getSuggestionList,
  hasSmartAttachmentFeature,
  canUseSuggestions,
  resolveCurrencyType
} from "../../common/libs/document.parser.service";

function getPosition(container, itemsLength = 0) {
  function getTop() {
    const inputElement = container.querySelector("input");
    const [top, parentTop] = [
      container.getBoundingClientRect().top,
      container.offsetParent.getBoundingClientRect().top
    ];
    const [labelHeight, inputHeight] = [
      inputElement ? inputElement.offsetTop - container.offsetTop : 20,
      inputElement?.offsetHeight || 36
    ];
    const [bottomSpacing, topSpacing, itemHeight] = [20, 6, 46];
    const inputTop = top + labelHeight + inputHeight - parentTop;
    const docHeight = document.body.getBoundingClientRect().height;
    const dropdownHeight = Math.min(itemHeight * itemsLength, 240);
    if (
      docHeight >
      inputTop + dropdownHeight + labelHeight + inputHeight + bottomSpacing
    ) {
      return inputTop + topSpacing;
    } else {
      return inputTop - topSpacing - dropdownHeight - inputHeight;
    }
  }
  const top = getTop();
  return {
    top,
    left: container.offsetLeft
  };
}
function SuggestionMenu(
  { onSelect, suggestionList, hasBlur = true, container, value = "" },
  ref
) {
  const [position, setPosition] = useState({});
  const [filterText, setFilterText] = useState(String(value));
  const [filtered, setFiltered] = useState(suggestionList);
  let floatRef = useRef(null);
  const updatePosition = useCallback(
    function updatePosition() {
      if (container && container.offsetParent) {
        setPosition(getPosition(container, filtered?.length));
      }
    },
    [container, filtered]
  );
  function hideMenu() {
    floatRef.current?.hide();
  }
  function showMenu() {
    updatePosition();
    floatRef.current?.show();
  }
  function handleChangeFromEvent(keyEvent, Type) {
    setTimeout(() => {
      if (
        keyEvent.target &&
        (keyEvent.target.value || keyEvent.target.contentEditable)
      ) {
        let value = keyEvent.target.value || keyEvent.target.innerText || "";
        if (NUMBER_FIELDS.includes(Type)) {
          value = value.replace(/[^\d.]/g, "");
        }
        setFilterText(String(value));
      }
    }, 10);
  }

  useImperativeHandle(ref, () => ({
    show: showMenu,
    hide: hideMenu,
    previous: () => {
      floatRef.current?.previous();
    },
    next: () => {
      floatRef.current?.next();
    },
    select: () => {
      floatRef.current?.select();
    },
    onKeyDown: (keyEvent, { Type }, canHandleEvents = true) => {
      handleChangeFromEvent(keyEvent, Type);
      if (canHandleEvents) {
        floatRef.current?.show();
        if (keyEvent.key === "ArrowDown") {
          floatRef.current?.next();
        } else if (keyEvent.key === "ArrowUp") {
          floatRef.current?.previous();
        } else if (keyEvent.key === "Enter") {
          floatRef.current?.select();
        } else if (keyEvent.key === "Tab") {
          hideMenu();
        }
      }
    }
  }));
  useLayoutEffect(
    function updatePositionOnFilter() {
      updatePosition();
    },
    [filtered, updatePosition]
  );

  useEffect(
    function updateFilteredList() {
      if (filterText?.length && suggestionList?.length) {
        const filterTextUpper = filterText.toUpperCase();
        const filtered = suggestionList.filter(
          ({ value }) =>
            String(value) !== filterText &&
            String(value).toUpperCase().indexOf(filterTextUpper) > -1
        );
        setFiltered(filtered);
      } else {
        setFiltered(suggestionList);
      }
    },
    [filterText, suggestionList]
  );

  function handleSelect(item) {
    onSelect(item.value, item.meta);
    hideMenu();
  }

  return (
    filtered?.length > 0 && (
      <FloatingMenu
        ref={floatRef}
        dataSource={filtered}
        className={styles.floating}
        emptyRenderer={() => null}
        position={FLOATING_MENU_POSITION.BOTTOM_LEFT_OVER}
        top={position.top ? position.top : "auto"}
        left={"auto"}
        closeOnOutsideClick={true}
        hideOnScroll={true}
        onSelect={(item) =>
          setTimeout(() => {
            onSelect();
            handleSelect(item);
          }, 100)
        }
        itemRenderer={(item, index) => (
          <MenuItem
            key={index}
            onMouseDown={(e) => (hasBlur ? handleSelect(item) : () => {})}
          >
            {item.value}
          </MenuItem>
        )}
        getContainer={() => container || document.body}
      />
    )
  );
}

SuggestionMenu.propTypes = {
  ref: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  container: PropTypes.shape({ offsetParent: PropTypes.element }),
  onSelect: PropTypes.func.isRequired,
  suggestionList: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  hasBlur: PropTypes.bool
};

export const DocumentSuggestions = forwardRef(SuggestionMenu);

export function DateSuggestionsHeader({ suggestionList }) {
  // eslint-disable-next-line react/prop-types
  return ({ onDateChange, closePopup }) => {
    function handleSelect(dateStr) {
      onDateChange(dayjs(new Date(dateStr)), true, closePopup);
    }
    return (
      suggestionList?.length > 0 && (
        <div className={styles.pickerHeaderWrapper}>
          <div className={`${styles.pickerHeaderTitle} tiny`}>
            <Chip size={CHIP_SIZES.TINY}>
              <Trans>Suggestions</Trans>
            </Chip>
          </div>
          <div className={`${styles.pickerHeaderContent} tiny`}>
            {suggestionList.slice(0, 3).map(({ value: item }, index) => (
              <Link
                href="#"
                key={index}
                className={styles.quickSelectLink}
                onClick={() => handleSelect(item)}
              >
                {item.split("T")[0]}
              </Link>
            ))}
          </div>
        </div>
      )
    );
  };
}
export function DateTimeSuggestionsHeader({ suggestionList }) {
  // eslint-disable-next-line react/prop-types
  return ({ onDateChange, closePopup }) => {
    function handleSelect(dateTimeStr) {
      onDateChange(dayjs(new Date(dateTimeStr)), true, closePopup);
    }
    return (
      suggestionList?.length > 0 && (
        <div className={styles.pickerHeaderWrapper}>
          <div className={`${styles.pickerHeaderTitle} tiny`}>
            <Chip size={CHIP_SIZES.TINY}>
              <Trans>Suggestions</Trans>
            </Chip>
          </div>
          <div className={`${styles.pickerHeaderContent} tiny`}>
            {suggestionList.slice(0, 3).map(({ value: item }, index) => (
              <Link
                href="#"
                key={index}
                className={styles.quickSelectLink}
                onClick={() => handleSelect(item)}
              >
                {item.replace("T", "")}
              </Link>
            ))}
          </div>
        </div>
      )
    );
  };
}
